import { useEffect, useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import CTABlock from "@/components/shared/CTABlock";
import HowWeBill from "@/components/home/HowWeBill";
import { fetchServices } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Services() {
  const [services, setServices] = useState([]);
  useEffect(() => { fetchServices().then((d) => setServices(d.services)).catch(() => {}); }, []);

  return (
    <div data-testid="page-services">
      <PageHeader
        eyebrow="What we run"
        title="The operating system for modern music marketing."
        description="Prulo doesn't run one-off influencer campaigns. We run the full marketing engine, creator networks, fan pages, auto-run seeding, lyric pages, and campaign analytics, the same infrastructure the biggest labels and artists rely on."
      />

      <section className="py-8 md:py-12 border-b border-white/5">
        <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {t: "Owned creator network", d: "2,000+ creators across every niche, not rented lists, an actual network we run."},
            {t: "Chart-tested playbooks", d: "Every framework we deploy has broken songs on Billboard, UK charts, Spotify Viral 50."},
            {t: "Full-stack execution", d: "Strategy, seeding, paid amplification, playlist support, fan pages, lyric pages, reporting."},
          ].map((d) => (
            <div key={d.t} className="border border-white/10 rounded-2xl p-6" data-testid={`services-differentiator-${d.t.split(' ')[0].toLowerCase()}`}>
              <div className="font-display font-semibold text-xl">{d.t}</div>
              <div className="mt-3 text-zinc-400 text-sm leading-relaxed">{d.d}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          {services.length > 0 && (
            <Tabs defaultValue={services[0].slug} className="w-full">
              <TabsList data-testid="services-tabs" className="bg-transparent border border-white/10 rounded-full p-1 h-auto flex-wrap">
                {services.map((s) => (
                  <TabsTrigger
                    key={s.slug}
                    value={s.slug}
                    data-testid={`services-tab-${s.slug}`}
                    className="rounded-full px-5 py-2 text-sm data-[state=active]:bg-white data-[state=active]:text-black text-zinc-400 gap-2"
                  >
                    {s.title}
                    {s.coming_soon && (
                      <span className="text-[9px] font-mono uppercase tracking-[0.2em] px-1.5 py-0.5 border border-white/25 rounded-full text-white/70">
                        Soon
                      </span>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>
              {services.map((s, i) => (
                <TabsContent key={s.slug} value={s.slug} className="mt-12">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                    <div className="md:col-span-4">
                      <div className="text-xs uppercase tracking-[0.24em] text-zinc-500 font-mono">0{i + 1}</div>
                      <div className="mt-4 flex items-center gap-3 flex-wrap">
                        <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight">{s.title}</h2>
                        {s.coming_soon && (
                          <span className="text-[10px] font-mono uppercase tracking-[0.24em] px-2.5 py-1 border border-white/25 rounded-full text-white/80" data-testid={`coming-soon-tab-${s.slug}`}>
                            Coming soon
                          </span>
                        )}
                      </div>
                      <p className="mt-6 text-zinc-400 text-lg">{s.tagline}</p>
                    </div>
                    <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {s.items.map((it, idx) => (
                        <div
                          key={it}
                          className="border border-white/10 rounded-2xl p-6 hover:border-white/25 transition-all"
                          data-testid={`service-item-${s.slug}-${idx}`}
                        >
                          <div className="text-xs font-mono text-zinc-500">0{idx + 1}</div>
                          <div className="mt-3 font-display font-semibold text-xl">{it}</div>
                          <div className="mt-2 text-sm text-zinc-500">
                            Built around measurable outcomes, impressions, saves, streams, and cultural velocity.
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>
      </section>

      <HowWeBill />

      <CTABlock />
    </div>
  );
}
